import React from "react";
import { Link } from "react-router-dom";

/**
 * Reusable BrownStone Jazz Club Logo Component
 * Displays the staggered "The Brown𝄞tone Jazz Club" text with proper styling
 * 
 * @param {Object} props
 * @param {string} props.size - 'sm' | 'md' | 'lg' | 'xl' - controls overall size
 * @param {boolean} props.linkToHome - whether clicking the logo navigates to home
 * @param {string} props.className - additional CSS classes
 */
const Logo = ({ size = "md", linkToHome = true, className = "" }) => {
  const sizeClasses = {
    sm: {
      container: "py-1",
      the: "text-[10px] -top-0.5 left-[0.4rem]",
      main: "text-lg",
      brown: "-top-1",
      treble: "-mx-0.5",
      tone: "top-0.5",
      jazzClub: "text-[10px] -bottom-0.5"
    },
    md: {
      container: "py-2",
      the: "text-xs sm:text-sm -top-1 left-[0.5rem] sm:left-[0.6rem]",
      main: "text-xl sm:text-2xl",
      brown: "-top-1.5 sm:-top-2",
      treble: "-mx-0.5",
      tone: "top-0.5 sm:top-1",
      jazzClub: "text-xs sm:text-sm -bottom-1"
    },
    lg: {
      container: "py-3",
      the: "text-sm sm:text-base -top-1 left-[0.7rem]",
      main: "text-2xl sm:text-3xl",
      brown: "-top-2 sm:-top-2.5",
      treble: "-mx-1",
      tone: "top-1 sm:top-1.5",
      jazzClub: "text-sm sm:text-base -bottom-1"
    },
    xl: {
      container: "py-4",
      the: "text-base sm:text-lg -top-1.5 left-[0.8rem]",
      main: "text-3xl sm:text-4xl md:text-5xl",
      brown: "-top-2.5 sm:-top-3",
      treble: "-mx-1",
      tone: "top-1.5 sm:top-2",
      jazzClub: "text-base sm:text-lg -bottom-1.5"
    }
  };

  const s = sizeClasses[size] || sizeClasses.md;

  const logoContent = (
    <div className={`relative ${s.container} ${className}`} style={{ fontFamily: "'Cookie', cursive" }}>
      <span className={`absolute ${s.the} text-white/70`}>The</span>
      <span className={`${s.main} tracking-normal inline-flex items-end`}>
        <span className={`text-platinum relative ${s.brown}`}>Brown</span>
        <span 
          className={`text-electric inline-block ${s.treble}`} 
          style={{ transform: 'scaleX(-1.3) scaleY(1.1)', fontFamily: 'serif' }}
        >
          𝄞
        </span>
        <span className={`text-platinum relative ${s.tone}`}>tone</span>
      </span>
      <span className={`absolute ${s.jazzClub} right-0 text-white/70`}>Jazz Club</span>
    </div>
  );

  if (linkToHome) {
    return (
      <Link to="/" className="flex items-center group" data-testid="logo-link">
        {logoContent}
      </Link>
    );
  }

  return logoContent;
};

export default Logo;
